/**
 * Message fragments every engine produces when an `import()` of a hashed chunk
 * fails. Matching on text is unavoidable: the thrown value is a plain `TypeError`
 * in every browser — no code, no name, no structured discriminator.
 *
 * Chromium: "Failed to fetch dynamically imported module",
 * "Failed to load module script: ... MIME type of \"text/html\"".
 * Firefox: "error loading dynamically imported module" (the network layer
 * reports NS_ERROR_CORRUPTED_CONTENT). WebKit: "Importing a module script
 * failed". `ChunkLoadError` covers webpack-bundled third-party code.
 */
const CHUNK_LOAD_ERROR_FRAGMENTS = [
  'failed to fetch dynamically imported module',
  'failed to load module script',
  'is not a valid javascript mime type',
  'error loading dynamically imported module',
  'ns_error_corrupted_content',
  'importing a module script failed',
  'module script failed to load',
  'loading chunk',
  'loading css chunk',
];

/** Max unwrapping depth — guards against a self-referential `cause` chain. */
const MAX_UNWRAP_DEPTH = 3;

/**
 * True when `error` (or anything it wraps) is a failed dynamic import of an
 * application chunk — i.e. the running bundle is older than what the server has.
 *
 * Accepts the raw rejection, an `Error` chain (`cause`) or a `NavigationError`
 * (`error`), because the same failure reaches us through more than one path. The
 * `rejection` key is vestigial: it unwrapped zone.js's uncaught-rejection wrapper,
 * and since B19 the app is zoneless — `provideBrowserGlobalErrorListeners()`
 * (`app.config.ts`) forwards `event.reason` directly. Kept because it costs one
 * unwrap step and a third-party wrapper of that shape would still be handled.
 */
export function isChunkLoadError(error: unknown): boolean {
  return unwrap(error).some(matches);
}

function unwrap(error: unknown, depth = 0): unknown[] {
  if (depth >= MAX_UNWRAP_DEPTH || !error || typeof error !== 'object') {
    return [error];
  }

  const wrapper = error as {
    rejection?: unknown;
    cause?: unknown;
    error?: unknown;
  };

  return [
    error,
    ...unwrapIfPresent(wrapper.rejection, depth),
    ...unwrapIfPresent(wrapper.cause, depth),
    ...unwrapIfPresent(wrapper.error, depth),
  ];
}

function unwrapIfPresent(value: unknown, depth: number): unknown[] {
  return value === undefined || value === null ? [] : unwrap(value, depth + 1);
}

function matches(error: unknown): boolean {
  if (typeof error === 'string') return includesFragment(error);
  if (!error || typeof error !== 'object') return false;

  const candidate = error as { name?: unknown; message?: unknown };
  if (candidate.name === 'ChunkLoadError') return true;

  return (
    typeof candidate.message === 'string' && includesFragment(candidate.message)
  );
}

function includesFragment(message: string): boolean {
  const normalized = message.toLowerCase();
  return CHUNK_LOAD_ERROR_FRAGMENTS.some((fragment) =>
    normalized.includes(fragment)
  );
}
